// src/page/Dashboard/useDashboard.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Patient } from "../../components/Card/props";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";

export interface RankedStudent {
  rank: number;
  userId: string;
  totalScore: number;
  submissionsCount: number;
  user: {
    name: string;
    profileImage: string;
    email: string;
    mobileNo: string;
  };
}

export interface RankingPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
}

export interface RankingFilters {
  testSeriesName: string;
  languageName: string;
  name: string;
}

const PAGE_LIMIT = 10;
const NAME_DEBOUNCE_MS = 400;

const useDashboard = () => {
  const [activePatientsCount] = useState(0);
  const [totalPatientsCount]  = useState(0);
  const [inactivePatientsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportContent] = useState<Patient[]>([]);

  const [dashboardDetails, setDashboardDetails] = useState({
    totalEBooks: 0,
    totalTest: 0,
    allStudents: 0,
  });
  const [submissionSummary, setSubmissionSummary] = useState<any>(null);

  // Ranking
  const [rankingData, setRankingData]       = useState<RankedStudent[]>([]);
  const [rankingLoading, setRankingLoading] = useState(true); // true only on first fetch
  const [rankingRefreshing, setRankingRefreshing] = useState(false); // subtle overlay on page/filter change
  const [rankingPagination, setRankingPagination] = useState<RankingPagination>({
    currentPage: 1,
    totalPages:  1,
    totalCount:  0,
    limit:       PAGE_LIMIT,
  });
  const [rankingPage, setRankingPage]         = useState(1);
  const [rankingFilters, setRankingFilters]   = useState<RankingFilters>({
    testSeriesName: "",
    languageName:   "",
    name:           "",
  });

  // Dropdown option lists
  const [testSeriesOptions, setTestSeriesOptions] = useState<{ id: string; name: string }[]>([]);
  const [languageOptions, setLanguageOptions]     = useState<string[]>([]);

  // Debounce ref for name search
  const nameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();

  // ── Fetch basic dashboard counts ──────────────────────────────────────────
  const fetchDashboardDetails = async () => {
    try {
      const data = await ApiService({ method: "GET", endpoint: apiPaths.dashboard });
      setDashboardDetails(data);
    } catch (error) {
      console.error("Error fetching dashboard details:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch submission summary charts ───────────────────────────────────────
  const fetchSubmissionSummary = async () => {
    try {
      const data = await ApiService({ method: "GET", endpoint: apiPaths.submissionSummary });
      setSubmissionSummary(data);
    } catch (error) {
      console.error("Error fetching submission summary:", error);
    }
  };

  // ── Fetch filter option lists ──────────────────────────────────────────────
  const fetchFilterOptions = async () => {
    try {
      const data = await ApiService({ method: "GET", endpoint: apiPaths.filterOptions });
      setTestSeriesOptions(data?.testSeries ?? []);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchLanguageOptions = async () => {
    try {
      const data = await ApiService({ method: "GET", endpoint: apiPaths.getlanguage });
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      setLanguageOptions(list.map((l: any) => l.languageName).filter(Boolean));
    } catch (error) {
      console.error("Error fetching language options:", error);
    }
  };

  // ── Core ranking fetch – called on page or filter change ──────────────────
  const fetchRanking = useCallback(
    async (filters: RankingFilters, page: number, isFirstLoad: boolean) => {
      if (isFirstLoad) {
        setRankingLoading(true);
      } else {
        setRankingRefreshing(true);
      }

      try {
        const params = new URLSearchParams();
        params.set("page",  String(page));
        params.set("limit", String(PAGE_LIMIT));
        if (filters.name)           params.set("name",           filters.name);
        if (filters.testSeriesName) params.set("testSeriesName", filters.testSeriesName);
        if (filters.languageName)   params.set("languageName",   filters.languageName);

        const data = await ApiService({
          method:   "GET",
          endpoint: `${apiPaths.dashboardRanking}?${params.toString()}`,
        });

        setRankingData(data?.data ?? []);
        setRankingPagination(
          data?.pagination ?? {
            currentPage: page,
            totalPages:  1,
            totalCount:  data?.totalUsers ?? 0,
            limit:       PAGE_LIMIT,
          }
        );
      } catch (error) {
        console.error("Error fetching student ranking:", error);
      } finally {
        setRankingLoading(false);
        setRankingRefreshing(false);
      }
    },
    []
  );

  // Initial load
  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;

    fetchDashboardDetails();
    fetchSubmissionSummary();
    fetchFilterOptions();
    fetchLanguageOptions();
    fetchRanking({ testSeriesName: "", languageName: "", name: "" }, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page changes (not first load)
  const isFirstPageEffect = useRef(true);
  useEffect(() => {
    if (isFirstPageEffect.current) {
      isFirstPageEffect.current = false;
      return;
    }
    fetchRanking(rankingFilters, rankingPage, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingPage]);

  // Re-fetch when dropdown filters change (reset to page 1)
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setRankingPage(1);
    fetchRanking(rankingFilters, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingFilters.testSeriesName, rankingFilters.languageName]);

  // ── Exposed actions ──────────────────────────────────────────────────────
  /** Change a dropdown API filter */
  const applyRankingFilter = (key: keyof RankingFilters, value: string) => {
    setRankingFilters((prev) => ({ ...prev, [key]: value }));
  };

  /** Debounced name update — fires API call after user stops typing */
  const updateNameSearch = (value: string) => {
    setRankingFilters((prev) => ({ ...prev, name: value }));
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      setRankingPage(1);
      fetchRanking({ ...rankingFilters, name: value }, 1, false);
    }, NAME_DEBOUNCE_MS);
  };

  /** Reset all filters and go back to page 1 */
  const clearRankingFilters = () => {
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    const reset: RankingFilters = { testSeriesName: "", languageName: "", name: "" };
    setRankingFilters(reset);
    setRankingPage(1);
    fetchRanking(reset, 1, false);
  };

  /** Change page (server-side) */
  const goToPage = (page: number) => {
    setRankingPage(page);
  };

  return {
    activePatientsCount,
    totalPatientsCount,
    inactivePatientsCount,
    loading,
    isModalOpen,
    reportContent,
    dashboardDetails,
    submissionSummary,
    rankingData,
    rankingLoading,
    rankingRefreshing,
    rankingPagination,
    rankingPage,
    rankingFilters,
    testSeriesOptions,
    languageOptions,
    applyRankingFilter,
    updateNameSearch,
    clearRankingFilters,
    goToPage,
    navigateToAnalysis: () => navigate("/test-submission-analysis"),
    setIsModalOpen,
  };
};

export default useDashboard;
